import { useCallback } from 'react';

/**
 * Custom hook to merge multiple refs into a single callback ref.
 * Handles both function refs and mutable ref objects.
 *
 * @param refs - Array of refs to merge (can include undefined refs)
 * @returns A callback ref that will set all provided refs
 *
 * @example
 * ```tsx
 * const internalRef = useRef<HTMLDivElement>(null);
 * const mergedRef = useMergeRefs(internalRef, forwardedRef);
 *
 * return <div ref={mergedRef} />;
 * ```
 */
export function useMergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
	return useCallback((instance: T | null) => {
		refs.forEach((ref) => {
			if (ref) {
				if (typeof ref === 'function') {
					ref(instance);
				} else {
					(ref as React.MutableRefObject<T | null>).current = instance;
				}
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, refs);
}
